import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import './Home.css';

function Home() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showNotification, setShowNotification] = useState(true);

  const handleSignOut = async () => {
    try { await signOut(); navigate('/'); }
    catch (error) { console.error('退出登录失败:', error); }
  };

  return (
    <div className="hm">
      {/* 导航栏 */}
      <nav className="hm-nav">
        <div className="hm-nav-inner">
          <div className="hm-nav-left">
            <span className="hm-logo">AIZIMU</span>
            <span className="hm-logo-sep">/</span>
            <span className="hm-logo-sub">方言字幕引擎</span>
          </div>
          <div className={`hm-links ${isMenuOpen ? 'hm-links--open' : ''}`}>
            <a href="#features" className="hm-link">功能</a>
            <a href="#dialects" className="hm-link">方言</a>
            <a href="#pricing" className="hm-link">定价</a>
          </div>
          <div className="hm-actions">
            <div className="theme-toggle" onClick={toggleTheme} title="切换主题">
              <span className={`theme-toggle-label theme-toggle-label--light ${theme === 'light' ? 'theme-toggle-label--active' : ''}`}>浅色</span>
              <span className={`theme-toggle-label theme-toggle-label--dark ${theme === 'dark' ? 'theme-toggle-label--active' : ''}`}>深色</span>
              <span className={`theme-toggle-indicator ${theme === 'dark' ? 'theme-toggle-indicator--dark' : ''}`} />
            </div>
            {user ? (
              <>
                <button onClick={() => navigate('/profile')} className="hm-btn">账户</button>
                <button onClick={() => navigate('/workspace')} className="hm-btn hm-btn--primary">工作台</button>
                <button onClick={handleSignOut} className="hm-btn hm-btn--red">退出</button>
              </>
            ) : (
              <button onClick={() => navigate('/auth')} className="hm-btn hm-btn--primary">登录</button>
            )}
          </div>
          <button className="hm-menu" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* 通知栏 */}
      {showNotification && (
        <div className="hm-notification">
          <div className="hm-notification-inner">
            <div className="hm-notification-content">
              <span className="hm-notification-icon">📢</span>
              <span className="hm-notification-text">
                <strong>系统提示：</strong>音频上传时长限制为 60 分钟，超过限制将无法生成字幕。
              </span>
            </div>
            <button
              className="hm-notification-close"
              onClick={() => setShowNotification(false)}
              aria-label="关闭通知"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="hm-hero">
        <div className="hm-hero-inner">
          <div className="hm-hero-left">
            <div className="hm-badge"><span className="hm-badge-dot" /><span>AI 驱动</span></div>
            <h1 className="hm-hero-title">方言音频<br />精准转字幕</h1>
            <p className="hm-hero-desc">
              上传音频，自动识别中文方言并生成字幕。
              支持粤语、四川话、上海话等主流方言，
              同时提供 SRT 多语言翻译。
            </p>
            <div className="hm-hero-actions">
              <button onClick={() => navigate(user ? '/workspace' : '/auth')} className="hm-btn hm-btn--primary hm-btn--lg">开始使用</button>
              <a href="#pricing" className="hm-btn hm-btn--outline hm-btn--lg">查看定价</a>
            </div>
          </div>
          <div className="hm-terminal">
            <div className="hm-terminal-bar">
              <span className="hm-dot hm-dot--r" /><span className="hm-dot hm-dot--y" /><span className="hm-dot hm-dot--g" />
            </div>
            <div className="hm-terminal-body">
              <div className="hm-term-line"><span className="hm-ps1">$</span><span className="hm-cmd">upload audio.mp3 --lang cantonese</span></div>
              <div className="hm-term-out">› 正在识别方言...</div>
              <div className="hm-term-out">› 生成字幕中 ████████░░ 80%</div>
              <div className="hm-term-out hm-term-ok">✓ 字幕生成完成 — 128 条字幕</div>
              <div className="hm-term-line" style={{marginTop:'8px'}}><span className="hm-ps1">$</span><span className="hm-cmd">translate output.srt --to en</span></div>
              <div className="hm-term-out hm-term-ok">✓ 翻译完成 — 已导出 output_en.srt</div>
              <div className="hm-term-line" style={{marginTop:'8px'}}><span className="hm-ps1">$</span><span className="hm-cursor" /></div>
            </div>
          </div>
        </div>
      </section>

      {/* 统计条 */}
      <section className="hm-stats">
        <div className="hm-stat"><span className="hm-stat-val">20+</span><span className="hm-stat-label">支持方言</span></div>
        <div className="hm-stat-div" />
        <div className="hm-stat"><span className="hm-stat-val">99%</span><span className="hm-stat-label">识别准确率</span></div>
        <div className="hm-stat-div" />
        <div className="hm-stat"><span className="hm-stat-val">50+</span><span className="hm-stat-label">翻译语言</span></div>
        <div className="hm-stat-div" />
        <div className="hm-stat"><span className="hm-stat-val">SRT</span><span className="hm-stat-label">标准格式</span></div>
      </section>

      {/* 功能区 */}
      <section className="hm-section" id="features">
        <p className="hm-sec-label">功能</p>
        <h2 className="hm-sec-title">从音频到字幕，一步到位</h2>
        <p className="hm-sec-desc">基于先进的语音识别模型，精准处理各类中文方言音频</p>
        <div className="hm-features">
          <div className="hm-fcard">
            <div className="hm-ficon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/>
              </svg>
            </div>
            <h3 className="hm-ftitle">方言语音识别</h3>
            <p className="hm-fdesc">支持粤语、四川话、上海话等多地中文方言的精准识别</p>
          </div>
          <div className="hm-fcard">
            <div className="hm-ficon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m5 8 6 6"/><path d="m4 14 6-6 2-3"/><path d="M2 5h12"/><path d="M7 2h1"/><path d="m22 22-5-10-5 10"/><path d="M14 18h6"/>
              </svg>
            </div>
            <h3 className="hm-ftitle">SRT 多语言翻译</h3>
            <p className="hm-fdesc">字幕文件直接翻译为 50+ 种语言，保留时间轴格式</p>
          </div>
          <div className="hm-fcard">
            <div className="hm-ficon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="18" height="18" x="3" y="3" rx="2"/><path d="M7 7h10"/><path d="M7 12h10"/><path d="M7 17h6"/>
              </svg>
            </div>
            <h3 className="hm-ftitle">在线字幕编辑</h3>
            <p className="hm-fdesc">可视化编辑器，支持逐条修改、查找替换、实时预览</p>
          </div>
        </div>
      </section>
      {/* 方言区 */}
      <section className="hm-section" id="dialects">
        <p className="hm-sec-label">支持方言</p>
        <h2 className="hm-sec-title">覆盖主流中文方言</h2>
        <p className="hm-sec-desc">持续扩展方言支持，让每一种声音都能被准确记录</p>
        <div className="hm-dialects">
          {['粤语','上海话','闽南语','西南官话','中原官话'].map(d => (
            <span key={d} className="hm-dtag">{d}</span>
          ))}
        </div>
      </section>

      {/* 定价区 */}
      <section className="hm-section" id="pricing">
        <p className="hm-sec-label">定价</p>
        <h2 className="hm-sec-title">按量计费，用多少付多少</h2>
        <p className="hm-sec-desc">注册即送 ¥1 体验余额，无月费，无隐藏费用</p>
        <div className="hm-pricing">
          <div className="hm-pcard">
            <span className="hm-pcard-label">音频字幕生成</span>
            <div><span className="hm-pcard-val">¥0.5</span><span className="hm-pcard-unit">/分钟</span></div>
            <p className="hm-pcard-desc">上传音频文件，AI 自动识别方言并生成 SRT 字幕</p>
            <ul className="hm-pcard-list">
              <li>支持 MP3 / WAV / M4A 等格式</li>
              <li>20+ 种中文方言识别</li>
              <li>自动断句与时间轴</li>
              <li>在线编辑与导出</li>
            </ul>
          </div>
          <div className="hm-pcard">
            <span className="hm-pcard-label">SRT 字幕翻译</span>
            <div><span className="hm-pcard-val">¥1.0</span><span className="hm-pcard-unit">/千字符</span></div>
            <p className="hm-pcard-desc">上传 SRT 文件，翻译为目标语言，保留完整时间轴</p>
            <ul className="hm-pcard-list">
              <li>50+ 种目标语言</li>
              <li>保留 SRT 时间轴格式</li>
              <li>最低消费 ¥0.1</li>
              <li>翻译结果可在线编辑</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="hm-cta">
        <div className="hm-cta-inner">
          <p className="hm-sec-label">开始</p>
          <h2 className="hm-cta-title">准备好了吗？</h2>
          <p className="hm-cta-desc">注册即送 ¥1 体验余额，立即体验方言字幕生成</p>
          <button onClick={() => navigate(user ? '/workspace' : '/auth')} className="hm-btn hm-btn--primary hm-btn--lg">免费试用</button>
        </div>
      </section>

      {/* Footer */}
      <footer className="hm-footer">
        <div className="hm-footer-inner">
          <div className="hm-footer-brand">
            <h3 className="hm-logo">AIZIMU</h3>
            <p className="hm-footer-desc">AI 驱动的中文方言字幕生成与翻译平台</p>
          </div>
          <div className="hm-footer-links">
            <div className="hm-footer-col">
              <h4>产品</h4>
              <a href="#features">功能</a>
              <a href="#dialects">方言</a>
              <a href="/workspace">工作台</a>
            </div>
            <div className="hm-footer-col">
              <h4>支持</h4>
              <a href="#help">帮助</a>
              <a href="#docs">文档</a>
              <a href="#contact">联系</a>
            </div>
            <div className="hm-footer-col">
              <h4>关于</h4>
              <a href="#privacy">隐私</a>
              <a href="#terms">条款</a>
            </div>
          </div>
        </div>
        <div className="hm-footer-bottom">
          <p>&copy; 2026 AIZIMU. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default Home;
